/**
 * Southern-hemisphere hops — New Zealand, Australia and South Africa.
 *
 * New Zealand figures follow NZ Hops and Hop Revolution grower data, Australian
 * figures follow Hop Products Australia, and the South African varieties follow
 * what the Elgin/George growers publish through their marketing agents. Values
 * are ranges because the southern crop swings hard year to year: alpha and total
 * oil in particular can move a couple of points between harvests, so treat the
 * band as the honest answer and the mid-point as the planning number.
 *
 * Cohumulone is expressed as a percentage *of alpha acid*, not of the cone, and
 * is left `null` on the newer releases where no grower breakdown exists yet.
 */

import type { Hop } from '../../schema/hop';

/** New Zealand, Australian and South African hop varieties. @see Hop */
export const southernHemisphereHops: readonly Hop[] = [
  {
    id: 'african-queen',
    name: 'African Queen',
    origin: 'South Africa',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 10, max: 13 },
    betaAcidPercent: { min: 4.5, max: 6 },
    cohumulonePercentOfAlpha: { min: 21, max: 27 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['southern-passion', 'galaxy', 'nelson-sauvin'],
    flavorTags: ['passionfruit', 'blackcurrant', 'lime', 'tropical', 'dank', 'stone-fruit'],
    description:
      'A dark, concentrated South African aroma hop that pushes blackcurrant and passionfruit over a resinous base. Intense enough that a little goes a long way in the whirlpool, and it blends well with softer tropical varieties rather than carrying a beer alone.',
    tags: ['south-africa', 'southern-hemisphere'],
    provenance: 'representative',
  },
  {
    id: 'dr-rudi',
    name: 'Dr. Rudi',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10, max: 13 },
    betaAcidPercent: { min: 7, max: 8.5 },
    cohumulonePercentOfAlpha: { min: 33, max: 38 },
    totalOilMlPer100g: { min: 1, max: 1.5 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 27,
      caryophyllenePercent: 11,
      farnesenePercent: 0.5,
    },
    substitutes: ['pacific-gem', 'southern-cross', 'magnum', 'columbus'],
    flavorTags: ['pine', 'resin', 'citrus', 'lemon-zest', 'herbal'],
    description:
      'New Zealand’s long-serving workhorse, sold as Super Alpha before it was renamed. Clean, high-yield bitterness with a pine-and-citrus late character that suits pale lagers and West Coast style IPAs equally.',
    tags: ['new-zealand', 'nz-hops', 'super-alpha', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/dr-rudi',
    provenance: 'spec-sheet',
  },
  {
    id: 'eclipse',
    name: 'Eclipse',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 14, max: 18 },
    betaAcidPercent: { min: 5.5, max: 7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.8, max: 3 },
    oilComposition: null,
    substitutes: ['galaxy', 'vic-secret', 'citra'],
    flavorTags: ['mandarin', 'citrus', 'pine', 'lime', 'resin'],
    description:
      'A recent Hop Products Australia release built around bright mandarin and lime over a pine backbone. High alpha for an aroma-driven hop, so it earns its keep in the whirlpool and dry hop without needing a separate bittering charge.',
    tags: ['australia', 'hpa', 'southern-hemisphere'],
    url: 'https://hops.com.au/eclipse/',
    provenance: 'representative',
  },
  {
    id: 'ella',
    name: 'Ella',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 16.5 },
    betaAcidPercent: { min: 5, max: 7 },
    cohumulonePercentOfAlpha: { min: 33, max: 38 },
    totalOilMlPer100g: { min: 1.8, max: 2.6 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 20,
      caryophyllenePercent: 9,
      farnesenePercent: 1,
    },
    substitutes: ['topaz', 'galaxy', 'hallertau-blanc'],
    flavorTags: ['floral', 'anise', 'spice', 'stone-fruit', 'grapefruit', 'tropical'],
    description:
      'Released as Stella and renamed, Ella shifts character with dose rate: restrained it reads floral and lightly spicy, pushed hard it turns tropical and grapefruity. A useful bridge hop when a Belgian or saison base needs modern fruit without losing its spice.',
    tags: ['australia', 'hpa', 'stella', 'southern-hemisphere'],
    url: 'https://hops.com.au/ella/',
    provenance: 'spec-sheet',
  },
  {
    id: 'enigma',
    name: 'Enigma',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 13, max: 17 },
    betaAcidPercent: { min: 4.5, max: 6 },
    cohumulonePercentOfAlpha: { min: 35, max: 40 },
    totalOilMlPer100g: { min: 2, max: 3 },
    oilComposition: null,
    substitutes: ['nelson-sauvin', 'galaxy', 'vic-secret'],
    flavorTags: ['redcurrant', 'raspberry', 'white-wine', 'melon', 'berry', 'stone-fruit'],
    description:
      'A Hop Products Australia selection with an unusual red-berry and white-wine profile that sits somewhere between Nelson Sauvin and a pilsner hop. Rewards restraint — heavy dosing pushes it towards a sharp, almost vinous edge.',
    tags: ['australia', 'hpa', 'southern-hemisphere'],
    url: 'https://hops.com.au/enigma/',
    provenance: 'spec-sheet',
  },
  {
    id: 'galaxy',
    name: 'Galaxy',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 11, max: 16 },
    betaAcidPercent: { min: 5, max: 6.9 },
    cohumulonePercentOfAlpha: { min: 32, max: 38 },
    totalOilMlPer100g: { min: 3, max: 5 },
    oilComposition: {
      myrcenePercent: 65,
      humulenePercent: 3,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['vic-secret', 'enigma', 'eclipse', 'nectaron'],
    flavorTags: ['passionfruit', 'peach', 'citrus', 'tropical', 'stone-fruit'],
    description:
      'The Australian benchmark for tropical hop aroma, with one of the highest total oil loads of any commercial variety. Passionfruit and peach dominate; because so much of the oil is myrcene it fades quickly, so keep the additions late and the beer fresh.',
    tags: ['australia', 'hpa', 'high-oil', 'southern-hemisphere'],
    url: 'https://hops.com.au/galaxy/',
    provenance: 'spec-sheet',
  },
  {
    id: 'green-bullet',
    name: 'Green Bullet',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 11, max: 14 },
    betaAcidPercent: { min: 6.5, max: 7.5 },
    cohumulonePercentOfAlpha: { min: 38, max: 42 },
    totalOilMlPer100g: { min: 1, max: 1.5 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 28,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['pacific-gem', 'dr-rudi', 'target', 'galena'],
    flavorTags: ['spicy', 'floral', 'raisin', 'resinous', 'earthy'],
    description:
      'New Zealand’s old high-alpha workhorse, a Smooth Cone seedling with a spicy, faintly raisiny bitterness. High cohumulone makes it assertive in the kettle; traditional in Kiwi lagers and dark ales rather than as a late hop.',
    tags: ['new-zealand', 'nz-hops', 'high-alpha', 'southern-hemisphere', 'classic'],
    url: 'https://nzhops.co.nz/products/green-bullet',
    provenance: 'spec-sheet',
  },
  {
    id: 'hpa-065',
    name: 'HPA-065',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 17.3, max: 17.3 },
    betaAcidPercent: { min: 9.7, max: 9.7 },
    cohumulonePercentOfAlpha: { min: 45, max: 45 },
    totalOilMlPer100g: { min: 3.9, max: 3.9 },
    oilComposition: {
      myrcenePercent: 67,
      humulenePercent: 8.1,
      caryophyllenePercent: 5.2,
      farnesenePercent: 0.1,
    },
    substitutes: ['topaz', 'vic-secret', 'galaxy', 'ella'],
    flavorTags: ['lime', 'ginger', 'pineapple', 'saffron'],
    description:
      'A Hop Products Australia trial selection at nearly 4 mL of oil and 17 % alpha, which is a lot of both. Lime and pineapple arrive with ginger and saffron behind them — spice-forward in a way few high-alpha hops are.',
    tags: ['australia', 'hpa', 'experimental', 'high-alpha'],
    provenance: 'representative',
  },
  {
    id: 'hpa-4029',
    name: 'HPA-4029',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 15 },
    betaAcidPercent: { min: 8, max: 10 },
    cohumulonePercentOfAlpha: { min: 41, max: 41 },
    totalOilMlPer100g: { min: 2.8, max: 3.2 },
    oilComposition: {
      myrcenePercent: 51,
      humulenePercent: 3.7,
      caryophyllenePercent: 7.8,
      farnesenePercent: 2.5,
    },
    substitutes: ['galaxy', 'vic-secret', 'topaz', 'enigma'],
    flavorTags: ['pineapple', 'passionfruit', 'grapefruit', 'tropical'],
    description:
      'The tropical end of the Australian trial program: pineapple, passionfruit and grapefruit stacked high enough to read as estery. Humulene is very low, so there is no herbal counterweight to it at all.',
    tags: ['australia', 'hpa', 'experimental', 'tropical'],
    provenance: 'representative',
  },
  {
    id: 'kohatu',
    name: 'Kohatu',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5.8, max: 7 },
    betaAcidPercent: { min: 4.8, max: 5.6 },
    cohumulonePercentOfAlpha: { min: 22, max: 26 },
    totalOilMlPer100g: { min: 0.8, max: 1.2 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 25,
      caryophyllenePercent: 9,
      farnesenePercent: 1,
    },
    substitutes: ['motueka', 'wai-iti', 'pacifica', 'riwaka'],
    flavorTags: ['tropical', 'pine', 'floral', 'lime', 'citrus'],
    description:
      'A gentle New Zealand aroma hop that layers pine and tropical fruit over a floral base. Low alpha and modest oil make it a finishing hop rather than a headliner — good in pale ales and lagers that want lift without a big fruit punch.',
    tags: ['new-zealand', 'nz-hops', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/kohatu',
    provenance: 'spec-sheet',
  },
  {
    id: 'luna',
    name: 'Luna',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13.2, max: 15 },
    betaAcidPercent: { min: 7.5, max: 8.2 },
    cohumulonePercentOfAlpha: { min: 35, max: 39 },
    totalOilMlPer100g: { min: 1.9, max: 2.5 },
    oilComposition: {
      myrcenePercent: 41,
      humulenePercent: 8,
      caryophyllenePercent: 9,
      farnesenePercent: 0.1,
    },
    substitutes: ['galaxy', 'vic-secret', 'ella', 'topaz'],
    flavorTags: ['mango', 'dark-berry', 'citrus', 'spicy'],
    description:
      'Trialled as HPA-033 and descended from Liberty crossed into high-alpha Australian stock. Mango and dark berry with a spiced citrus edge, and a polyphenol load its growers credit for the soft mouthfeel it leaves behind.',
    tags: ['australia', 'hpa', 'modern'],
    provenance: 'representative',
  },
  {
    id: 'motueka',
    name: 'Motueka',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 6.5, max: 8.5 },
    betaAcidPercent: { min: 5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 27, max: 31 },
    totalOilMlPer100g: { min: 0.8, max: 1.2 },
    oilComposition: {
      myrcenePercent: 47,
      humulenePercent: 12,
      caryophyllenePercent: 6,
      farnesenePercent: 5,
    },
    substitutes: ['saaz', 'kohatu', 'riwaka', 'wai-iti'],
    flavorTags: ['lime', 'lemon', 'tropical', 'citrus', 'noble'],
    description:
      'A Saaz-descended New Zealand hop with a distinct fresh lime and lemon-zest character over a noble spine. Its farnesene level is high for a modern variety, which is part of why it reads clean and aromatic rather than heavy.',
    tags: ['new-zealand', 'nz-hops', 'saaz-derived', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/motueka',
    provenance: 'spec-sheet',
  },
  {
    id: 'moutere',
    name: 'Moutere',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 15, max: 19 },
    betaAcidPercent: { min: 7, max: 9 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2.5, max: 4 },
    oilComposition: null,
    substitutes: ['waimea', 'nectaron', 'rakau', 'superdelic'],
    flavorTags: ['stone-fruit', 'citrus', 'marmalade', 'pine', 'tropical'],
    description:
      'The high-oil member of the New Zealand line-up: big alpha paired with a total oil load few varieties reach, giving marmalade and stone fruit with a resinous finish. Dose carefully in the dry hop, as the oil concentration makes it easy to overshoot.',
    tags: ['new-zealand', 'nz-hops', 'high-oil', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/moutere%E2%84%A2',
    provenance: 'representative',
  },
  {
    id: 'nectaron',
    name: 'Nectaron',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 8, max: 12 },
    betaAcidPercent: { min: 4, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 27 },
    totalOilMlPer100g: { min: 2, max: 3 },
    oilComposition: null,
    substitutes: ['galaxy', 'waimea', 'nelson-sauvin', 'riwaka'],
    flavorTags: ['passionfruit', 'pineapple', 'stone-fruit', 'tropical', 'peach'],
    description:
      'A Plant & Food Research release that arrived as New Zealand’s answer to the big tropical Australians. Dense passionfruit and pineapple with a soft stone-fruit tail, and enough oil to carry a hazy IPA on its own.',
    tags: ['new-zealand', 'nz-hops', 'hop-revolution', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/nectaron',
    provenance: 'spec-sheet',
  },
  {
    id: 'nelson-sauvin',
    name: 'Nelson Sauvin',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10, max: 13 },
    betaAcidPercent: { min: 6, max: 8 },
    cohumulonePercentOfAlpha: { min: 22, max: 26 },
    totalOilMlPer100g: { min: 1, max: 1.5 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 36,
      caryophyllenePercent: 12,
      farnesenePercent: 1,
    },
    substitutes: ['enigma', 'nectaron', 'hallertau-blanc', 'african-queen'],
    flavorTags: ['white-wine', 'gooseberry', 'passionfruit', 'grape', 'tropical', 'stone-fruit'],
    description:
      'Named for the Sauvignon Blanc grapes it echoes, this is the most distinctive hop New Zealand grows: gooseberry, crushed grape and a dry white-wine edge. Polarising at high rates — many brewers blend it rather than let it stand alone.',
    tags: ['new-zealand', 'nz-hops', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/nelson-sauvin',
    provenance: 'spec-sheet',
  },
  {
    id: 'pacific-gem',
    name: 'Pacific Gem',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 13, max: 16 },
    betaAcidPercent: { min: 7, max: 9 },
    cohumulonePercentOfAlpha: { min: 36, max: 40 },
    totalOilMlPer100g: { min: 1, max: 1.4 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 25,
      caryophyllenePercent: 11,
      farnesenePercent: 1,
    },
    substitutes: ['dr-rudi', 'pacific-jade', 'southern-cross', 'galena'],
    flavorTags: ['blackberry', 'oak', 'berry', 'woody', 'resin'],
    description:
      'A firm New Zealand bittering hop with an oaky, blackberry note that shows up in darker beers and stouts. The relatively high cohumulone gives an assertive bitterness, so it suits robust malt bills better than delicate lagers.',
    tags: ['new-zealand', 'nz-hops', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/pacific-gem',
    provenance: 'spec-sheet',
  },
  {
    id: 'pacific-jade',
    name: 'Pacific Jade',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 14 },
    betaAcidPercent: { min: 7, max: 8.5 },
    cohumulonePercentOfAlpha: { min: 23, max: 27 },
    totalOilMlPer100g: { min: 1.2, max: 1.8 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 27,
      caryophyllenePercent: 10,
      farnesenePercent: 1,
    },
    substitutes: ['southern-cross', 'pacific-gem', 'target', 'dr-rudi'],
    flavorTags: ['black-pepper', 'citrus', 'herbal', 'lemon', 'spice'],
    description:
      'Low cohumulone gives Pacific Jade a notably soft bitterness for its alpha, and late additions bring fresh-cracked black pepper alongside lemon and herb. A tidy single-hop choice for pale ales that want structure rather than fruit.',
    tags: ['new-zealand', 'nz-hops', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/pacific-jade',
    provenance: 'spec-sheet',
  },
  {
    id: 'pacific-sunrise',
    name: 'Pacific Sunrise',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 15 },
    betaAcidPercent: { min: 5, max: 7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['pacific-gem', 'green-bullet', 'dr-rudi', 'southern-cross'],
    flavorTags: ['citrus', 'berry', 'pine', 'floral'],
    description:
      'A New Zealand high-alpha that does not taste like one: the bittering is clean enough for a kettle charge, and what comes with it is citrus and dark berry over pine and hay. Useful where a beer wants its IBU and its aroma from the same hop.',
    tags: ['nz', 'new-zealand', 'high-alpha', 'dual'],
    provenance: 'representative',
  },
  {
    id: 'pacifica',
    name: 'Pacifica',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 6.5 },
    betaAcidPercent: { min: 5.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 24, max: 28 },
    totalOilMlPer100g: { min: 1, max: 1.9 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 25,
      caryophyllenePercent: 10,
      farnesenePercent: 1,
    },
    substitutes: ['hallertauer-mittelfrueh', 'motueka', 'kohatu', 'hersbrucker'],
    flavorTags: ['orange-marmalade', 'citrus', 'floral', 'noble', 'herbal'],
    description:
      'A Hallertau Mittelfrüh descendant grown in Nelson, keeping the noble herbal frame but adding orange marmalade on top. Works as a New Zealand stand-in for German aroma hops in pilsners, kölsch and altbier.',
    tags: ['new-zealand', 'nz-hops', 'pacific-hallertau', 'noble-derived', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/pacifica',
    provenance: 'spec-sheet',
  },
  {
    id: 'paradisi',
    name: 'Paradisi',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 15.2, max: 15.2 },
    betaAcidPercent: { min: 6.2, max: 6.2 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.9, max: 1.9 },
    oilComposition: null,
    substitutes: ['nelson-sauvin', 'riwaka', 'superdelic'],
    flavorTags: ['grapefruit', 'guava', 'resinous', 'pine'],
    description:
      'Pink grapefruit and guava over a resinous, lightly piney base, at an alpha high enough to bitter with. Grown around Nelson Lakes, where the 2025 harvest was picked for exactly that tropical-and-sweet complexity.',
    tags: ['nz', 'new-zealand', 'modern', 'high-alpha'],
    provenance: 'representative',
  },
  {
    id: 'peacharine',
    name: 'Peacharine',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9, max: 12 },
    betaAcidPercent: { min: 4.5, max: 6.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.4, max: 2.2 },
    oilComposition: null,
    substitutes: ['rakau', 'nectaron', 'el-dorado', 'callista'],
    flavorTags: ['peach', 'nectarine', 'citrus', 'lime', 'stone-fruit', 'floral'],
    description:
      'A Freestyle Hops advanced selection with a straight peach-and-nectarine signature over lime zest and a soft floral base. Still settling commercially, so published figures are thin; mid-to-late harvest lots are the ones growers keep coming back to.',
    tags: ['new-zealand', 'freestyle-hops', 'southern-hemisphere', 'modern'],
    provenance: 'representative',
  },
  {
    id: 'pride-of-ringwood',
    name: 'Pride of Ringwood',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 7, max: 11 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 32, max: 39 },
    totalOilMlPer100g: { min: 1, max: 2 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 4,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 44,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['super-pride', 'galena', 'cluster', 'target'],
    flavorTags: ['cedar', 'herbal', 'earthy', 'woody'],
    description:
      'The hop that bittered Australian lager for half a century: cedar, herbs and a rustic earthiness, with high cohumulone that reads as a firm, slightly coarse bitterness. Super Pride is the modern high-alpha child; keep Pride itself for historical recipes and dark ales.',
    tags: ['australia', 'hpa', 'heritage', 'southern-hemisphere', 'classic'],
    url: 'https://charlesfaram.com/hop-oracle/pride-of-ringwood/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rakau',
    name: 'Rakau',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9, max: 11.5 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 26 },
    totalOilMlPer100g: { min: 1.8, max: 2.4 },
    oilComposition: {
      myrcenePercent: 55,
      humulenePercent: 15,
      caryophyllenePercent: 8,
      farnesenePercent: 1,
    },
    substitutes: ['waimea', 'nectaron', 'moutere', 'galaxy'],
    flavorTags: ['apricot', 'stone-fruit', 'resin', 'tropical', 'pine'],
    description:
      'Formerly sold as Alpha Aroma, Rakau leads with clean apricot and stone fruit over a resinous base. Good oil for its alpha, which makes it an efficient whirlpool hop in pale ales and fruit-forward IPAs.',
    tags: ['new-zealand', 'nz-hops', 'alpha-aroma', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/rakau',
    provenance: 'spec-sheet',
  },
  {
    id: 'riwaka',
    name: 'Riwaka',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4.5, max: 6.5 },
    betaAcidPercent: { min: 4, max: 5 },
    cohumulonePercentOfAlpha: { min: 28, max: 32 },
    totalOilMlPer100g: { min: 0.8, max: 1.3 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 15,
      caryophyllenePercent: 7,
      farnesenePercent: 2,
    },
    substitutes: ['motueka', 'nectaron', 'kohatu', 'saaz'],
    flavorTags: ['passionfruit', 'grapefruit', 'citrus', 'tropical', 'zesty'],
    description:
      'A Saaz-derived aroma hop, once traded as D Saaz, famous for an intense grapefruit and passionfruit burst that is far bigger than its low alpha suggests. Supply is chronically short, so treat it as a feature hop and buy it when you see it.',
    tags: ['new-zealand', 'nz-hops', 'd-saaz', 'saaz-derived', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/riwaka%E2%84%A2',
    provenance: 'spec-sheet',
  },
  {
    id: 'southern-cross',
    name: 'Southern Cross',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11, max: 14 },
    betaAcidPercent: { min: 5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 24, max: 28 },
    totalOilMlPer100g: { min: 0.8, max: 1.2 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 22,
      caryophyllenePercent: 9,
      farnesenePercent: 2,
    },
    substitutes: ['pacific-jade', 'dr-rudi', 'pacific-gem'],
    flavorTags: ['lemon-peel', 'pine', 'citrus', 'spice', 'resin'],
    description:
      'A clean dual-purpose New Zealander with lemon peel and pine on the nose and a smooth bitterness thanks to modest cohumulone. Comfortable across the whole boil, from a single bittering charge to a late aroma addition.',
    tags: ['new-zealand', 'nz-hops', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/southern-cross',
    provenance: 'spec-sheet',
  },
  {
    id: 'southern-passion',
    name: 'Southern Passion',
    origin: 'South Africa',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 10, max: 12.5 },
    betaAcidPercent: { min: 3.5, max: 4.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.4, max: 2 },
    oilComposition: null,
    substitutes: ['african-queen', 'galaxy', 'citra', 'nectaron'],
    flavorTags: ['passionfruit', 'guava', 'citrus', 'tropical', 'melon'],
    description:
      'A South African aroma variety bred for tropical intensity, leaning on passionfruit and guava with a soft citrus edge. Cleaner and sweeter than African Queen, which makes the two a natural pair in a hazy IPA hop bill.',
    tags: ['south-africa', 'southern-hemisphere'],
    provenance: 'representative',
  },
  {
    id: 'southern-star',
    name: 'Southern Star',
    origin: 'South Africa',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 16 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.2, max: 2.2 },
    oilComposition: null,
    substitutes: ['southern-passion', 'african-queen', 'galaxy', 'citra'],
    flavorTags: ['berry', 'tropical', 'citrus', 'spicy', 'stone-fruit'],
    description:
      'A South African dual hop with berry and tropical fruit over a spicy citrus edge, sitting between Southern Passion’s sweetness and African Queen’s darker currant. Enough alpha to bitter a pale ale; the interesting part is still the late addition.',
    tags: ['south-africa', 'southern-hemisphere', 'dual-purpose'],
    provenance: 'representative',
  },
  {
    id: 'southern-tropic',
    name: 'Southern Tropic',
    origin: 'South Africa',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 17, max: 18 },
    betaAcidPercent: { min: 6.4, max: 6.6 },
    cohumulonePercentOfAlpha: { min: 26, max: 28 },
    totalOilMlPer100g: { min: 1.3, max: 1.3 },
    oilComposition: {
      myrcenePercent: null,
      humulenePercent: null,
      caryophyllenePercent: null,
      farnesenePercent: 9.6,
    },
    substitutes: ['southern-passion', 'african-queen', 'southern-star'],
    flavorTags: ['guava', 'mango', 'lychee', 'pine', 'lemongrass'],
    description:
      'One of the highest-alpha hops South Africa grows, and it does not spend that alpha quietly: guava, mango and lychee arrive over pine, resin and lemongrass, with a coconut edge at the end. A little in the whirlpool goes a long way.',
    tags: ['south-africa', 'high-alpha', 'tropical'],
    provenance: 'representative',
  },
  {
    id: 'superdelic',
    name: 'Superdelic',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 11, max: 14 },
    betaAcidPercent: { min: 5, max: 7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.8, max: 2.8 },
    oilComposition: null,
    substitutes: ['nectaron', 'nelson-sauvin', 'galaxy', 'waimea'],
    flavorTags: ['grape', 'candy', 'passionfruit', 'blackcurrant', 'watermelon', 'tropical'],
    description:
      'The NZ Hops release trialled as HORT4337, notable for a sweet grape-candy and blackcurrant character unlike anything else in the New Zealand range. Newly commercial, so published figures are still settling between crop years.',
    tags: ['new-zealand', 'nz-hops', 'hort4337', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/superdelic',
    provenance: 'representative',
  },
  {
    id: 'super-pride',
    name: 'Super Pride',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 13, max: 17 },
    betaAcidPercent: { min: 6.4, max: 7.8 },
    cohumulonePercentOfAlpha: { min: 22, max: 27 },
    totalOilMlPer100g: { min: 1.8, max: 2.6 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 20,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['magnum', 'warrior', 'dr-rudi', 'galena'],
    flavorTags: ['resin', 'pine', 'herbal', 'earthy', 'woody'],
    description:
      'Hop Products Australia’s high-alpha selection out of the Pride of Ringwood line, bred to deliver clean bitterness at scale. Low cohumulone for its alpha keeps the bitterness rounded, and it is almost always used as a kettle hop.',
    tags: ['australia', 'hpa', 'pride-of-ringwood', 'southern-hemisphere'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/super-pride',
    provenance: 'spec-sheet',
  },
  {
    id: 'taiheke',
    name: 'Taiheke',
    origin: 'New Zealand',
    growingRegion: 'Nelson',
    cultivar: 'cascade',
    purpose: 'aroma',
    alphaAcidPercent: { min: 6, max: 8.5 },
    betaAcidPercent: { min: 5.5, max: 7 },
    cohumulonePercentOfAlpha: { min: 29, max: 34 },
    totalOilMlPer100g: { min: 1, max: 1.6 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 13,
      caryophyllenePercent: 5,
      farnesenePercent: 6,
    },
    substitutes: ['cascade', 'centennial', 'pacifica', 'motueka'],
    flavorTags: ['grapefruit', 'citrus', 'floral', 'lime', 'zesty'],
    description:
      'The New Zealand-grown selection of Cascade, sold under its Māori name. It keeps the familiar grapefruit-and-floral signature but comes across brighter and more lime-edged than the American original, thanks to Nelson terroir.',
    tags: ['new-zealand', 'nz-hops', 'cascade-selection', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/taiheke',
    provenance: 'representative',
  },
  {
    id: 'topaz',
    name: 'Topaz',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 14, max: 18 },
    betaAcidPercent: { min: 6, max: 7.5 },
    cohumulonePercentOfAlpha: { min: 45, max: 52 },
    totalOilMlPer100g: { min: 1.4, max: 2.2 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 15,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    substitutes: ['ella', 'galaxy', 'enigma', 'vic-secret'],
    flavorTags: ['tropical', 'lychee', 'grassy', 'resin', 'clove', 'stone-fruit'],
    description:
      'High alpha with unusually high cohumulone, so Topaz bitters hard and can turn grassy or resinous if overdosed. Used late and in moderation it gives lychee and tropical fruit with a clove-like spice underneath.',
    tags: ['australia', 'hpa', 'southern-hemisphere'],
    url: 'https://hops.com.au/topaz/',
    provenance: 'spec-sheet',
  },
  {
    id: 'vic-secret',
    name: 'Vic Secret',
    origin: 'Australia',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 14, max: 17 },
    betaAcidPercent: { min: 6, max: 8.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2, max: 3 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 15,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['galaxy', 'eclipse', 'enigma', 'topaz'],
    flavorTags: ['pineapple', 'passionfruit', 'pine', 'herbal', 'tropical'],
    description:
      'A sibling to Galaxy in style but greener, trading some of the passionfruit for pineapple and pine. High alpha and high oil together make it a strong single-hop candidate for a modern Australian IPA.',
    tags: ['australia', 'hpa', 'southern-hemisphere'],
    url: 'https://hops.com.au/vic-secret/',
    provenance: 'spec-sheet',
  },
  {
    id: 'wai-iti',
    name: 'Wai-iti',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 2.5, max: 4.5 },
    betaAcidPercent: { min: 4.5, max: 6 },
    cohumulonePercentOfAlpha: { min: 20, max: 25 },
    totalOilMlPer100g: { min: 1.6, max: 2.4 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 20,
      caryophyllenePercent: 9,
      farnesenePercent: 1,
    },
    substitutes: ['motueka', 'kohatu', 'pacifica', 'riwaka'],
    flavorTags: ['peach', 'apricot', 'lime', 'stone-fruit', 'citrus'],
    description:
      'Very low alpha paired with a surprisingly generous oil load, which makes Wai-iti a pure aroma hop you can dose heavily without adding bitterness. Soft peach and lime, ideal for delicate pale ales, lagers and session beers.',
    tags: ['new-zealand', 'nz-hops', 'low-alpha', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/wai-iti',
    provenance: 'spec-sheet',
  },
  {
    id: 'waimea',
    name: 'Waimea',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 17 },
    betaAcidPercent: { min: 7, max: 9 },
    cohumulonePercentOfAlpha: { min: 21, max: 25 },
    totalOilMlPer100g: { min: 2, max: 2.8 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 15,
      caryophyllenePercent: 8,
      farnesenePercent: 1,
    },
    substitutes: ['rakau', 'nectaron', 'moutere', 'superdelic'],
    flavorTags: ['tangelo', 'citrus', 'grapefruit', 'pine', 'tropical'],
    description:
      'One of the few New Zealand hops that genuinely does both jobs: high alpha with low cohumulone for clean bitterness, plus enough oil for a big tangelo and grapefruit dry hop. A sensible single-variety backbone for a New Zealand IPA.',
    tags: ['new-zealand', 'nz-hops', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/waimea',
    provenance: 'spec-sheet',
  },
  {
    id: 'wakatu',
    name: 'Wakatu',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 6.5, max: 8.5 },
    betaAcidPercent: { min: 8, max: 9 },
    cohumulonePercentOfAlpha: { min: 28, max: 30 },
    totalOilMlPer100g: { min: 0.8, max: 1.2 },
    oilComposition: {
      myrcenePercent: 36,
      humulenePercent: 17,
      caryophyllenePercent: 8,
      farnesenePercent: 7,
    },
    substitutes: ['pacifica', 'motueka', 'hallertauer-mittelfrueh', 'saaz'],
    flavorTags: ['lime', 'floral', 'spicy', 'noble', 'citrus'],
    description:
      'Formerly Hallertau Aroma, a New Zealand Hallertau descendant with unusually high beta acid and a fresh lime-and-floral lift over a noble spice. Excellent late in pilsners and pale lagers, and a useful bridge hop when a New Zealand IPA needs some restraint.',
    tags: ['new-zealand', 'nz-hops', 'hallertau-aroma', 'noble-derived', 'southern-hemisphere'],
    url: 'https://nzhops.co.nz/products/wakatu-1',
    provenance: 'spec-sheet',
  },
  {
    id: 'whakatu-koura',
    name: 'Whakatū Kōura',
    origin: 'New Zealand',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 5 },
    betaAcidPercent: { min: 7, max: 7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: ['wakatu'],
    flavorTags: ['floral', 'citrus', 'lime', 'white-grape'],
    description:
      'Not a variety but a preparation: Wakatu hops blended with Phantasm, the thiol-releasing powder made from Marlborough Sauvignon Blanc skins. The point is what the yeast does with it — free thiols that read as lime zest and white grape — so it goes in where fermentation can act on it. Total oil is not published for the blend.',
    tags: ['nz', 'thiol', 'phantasm', 'product'],
    kind: 'product',
    defaultForm: 'pellet',
    provenance: 'representative',
  },
];
